
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Profile } from "@/types/profile";
import { Mail, LogIn } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface ProfileHeaderProps {
  profile: Profile;
}

export const ProfileHeader = ({ profile }: ProfileHeaderProps) => {
  const navigate = useNavigate();

  // Safe way to get the first letter for the avatar fallback
  const getAvatarFallback = () => {
    if (profile.full_name && profile.full_name.length > 0) {
      return profile.full_name[0].toUpperCase();
    }
    if (profile.role === 'creator' && profile.username && profile.username.length > 0) {
      return profile.username[0].toUpperCase();
    }
    return 'U';
  };

  return (
    <>
      <div 
        className="h-56 w-full bg-gradient-to-r from-blue-600 to-purple-600 relative" 
        style={profile.banner_url ? { backgroundImage: `url(${profile.banner_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
      >
        <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-background to-transparent"></div>
        
        {/* Updated Student Login Button */}
        <div className="absolute top-4 right-4 z-20">
          <Button
            onClick={() => navigate('/login')}
            variant="outline"
            className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 text-white gap-2 shadow-lg"
          >
            <LogIn className="h-4 w-4" />
            Student Login
          </Button>
        </div>
      </div>

      {/* Profile Info */}
      <div className="container mx-auto px-4 relative">
        <div className="flex flex-col md:flex-row gap-6 -mt-16 mb-12">
          {/* Avatar and Info */}
          <div className="flex-shrink-0 flex flex-col items-center md:items-start">
            <Avatar className="w-32 h-32 border-4 border-background shadow-lg">
              <AvatarImage src={profile.avatar_url || undefined} />
              <AvatarFallback>{getAvatarFallback()}</AvatarFallback>
            </Avatar>

            <h1 className="text-2xl md:text-3xl font-bold mt-4">
              {profile.full_name || (profile.role === 'creator' ? profile.username : "User")}
            </h1>

            {profile.bio && (
              <p className="text-muted-foreground mt-2 text-center md:text-left max-w-md">
                {profile.bio}
              </p>
            )}

            {/* Contact Button */}
            {profile.email && (
              <Button 
                className="mt-4"
                onClick={() => window.location.href = `mailto:${profile.email}`}
              >
                <Mail className="mr-2 h-4 w-4" />
                Contact Creator
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
